import type { Realm } from '@/services/types/Realm';
import { ADMIN_LOGIN_PATH } from '@/services/adminSession';

/**
 * Which door this tab came in by: the public site and its cabinet, signed in through MPass, or the
 * administration's own host, where SYSTEM_ADMIN accounts sign in with a password (ADM-1). The
 * administration host is a separate vhost restricted by network (DevOps); the SPA only needs to
 * know it is on it, to send a signed-out visitor to the right sign-in screen.
 *
 * The hosts come from the build (VITE_ADMIN_HOSTS, comma-separated). On a developer's machine
 * there is one host for both, so `?realm=admin` on localhost switches the tab to the admin realm
 * and `?realm=public` back; the choice is kept for the tab, as the sign-in round trip loses the
 * query.
 */

export const REALM = { public: 'public', admin: 'admin' } as const;

const REALM_PARAM = 'realm';
const REMEMBERED_KEY = 'akis.realm';
const DEV_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

/** The administration hosts a build names: trimmed, lower-case, blanks dropped. */
export function adminHostsOf(value: string | undefined): string[] {
  return (value ?? '')
    .split(',')
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean);
}

/** Whether a host is a developer's own machine, with or without its port. */
export function isDevHost(host: string): boolean {
  const name = host.toLowerCase().replace(/:\d+$/, '');
  return DEV_HOSTS.includes(name);
}

/**
 * The realm a page is on: an administration host by name (with or without its port); on a
 * developer's machine, what `?realm=` says, or what this tab remembered from it; the public realm
 * everywhere else. A remembered choice never applies outside a developer's machine.
 */
export function realmOf(input: {
  host: string;
  search: string;
  adminHosts: readonly string[];
  remembered: string | null;
}): Realm {
  const host = input.host.toLowerCase();
  const name = host.replace(/:\d+$/, '');
  if (input.adminHosts.includes(host) || input.adminHosts.includes(name)) return REALM.admin;
  if (!isDevHost(host)) return REALM.public;
  const asked = new URLSearchParams(input.search).get(REALM_PARAM);
  if (asked === REALM.admin || asked === REALM.public) return asked;
  return input.remembered === REALM.admin ? REALM.admin : REALM.public;
}

function readRemembered(): string | null {
  try {
    return window.sessionStorage.getItem(REMEMBERED_KEY);
  } catch {
    return null;
  }
}

/** The realm of the page this tab is on, remembering a developer's `?realm=` for the tab. */
export function currentRealm(): Realm {
  if (typeof window === 'undefined') return REALM.public;
  const realm = realmOf({
    host: window.location.host,
    search: window.location.search,
    adminHosts: adminHostsOf(import.meta.env.VITE_ADMIN_HOSTS as string | undefined),
    remembered: readRemembered(),
  });
  if (
    isDevHost(window.location.host) &&
    new URLSearchParams(window.location.search).has(REALM_PARAM)
  ) {
    try {
      window.sessionStorage.setItem(REMEMBERED_KEY, realm);
    } catch {
      // A tab that cannot store keeps the realm for this page alone.
    }
  }
  return realm;
}

/**
 * Where a credential's holder signs in, as the window that gives the credential tells its giver:
 * on a developer's machine this server's own address, switched to the admin realm; otherwise the
 * first administration host the build names, over HTTPS. Null when the build names none — the
 * operator decides that host, and no address here would be the right one.
 */
export function adminLoginUrl(
  adminHosts: readonly string[],
  current: { host: string; origin: string },
): string | null {
  if (isDevHost(current.host)) return `${current.origin}${ADMIN_LOGIN_PATH}?realm=admin`;
  if (adminHosts.length > 0) return `https://${adminHosts[0]}${ADMIN_LOGIN_PATH}`;
  return null;
}

/** adminLoginUrl for this page and this build. */
export function currentAdminLoginUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return adminLoginUrl(adminHostsOf(import.meta.env.VITE_ADMIN_HOSTS as string | undefined), {
    host: window.location.host,
    origin: window.location.origin,
  });
}
